import { apiFetch, getIdToken } from './auth-service';

export interface StartupDocument {
  _id: string;
  title: string;
  fileUrl: string;
  fileKey: string;
  fileName: string;
  fileSize: number;
  uploadedAt: string;
}

export interface CoFounder {
  name: string;
  email: string;
  role: string;
  status: 'pending' | 'accepted' | 'declined';
  isFocalTrainee: boolean;
}

export interface StartupDoc {
  _id: string;
  founderId: string;
  name: string;
  tagline: string;
  description: string;
  industry: string;
  stage: string;
  website: string;
  teamSize: number;
  foundingYear: number;
  problemStatement: string;
  solution: string;
  targetMarket: string;
  revenueModel: string;
  traction: string;
  documents: StartupDocument[];
  coFounders: CoFounder[];
  createdAt: string;
  updatedAt: string;
}

export const createStartup = async (data: Partial<StartupDoc> & Record<string, any>): Promise<StartupDoc> => {
  const r = await apiFetch.post<{ startup: StartupDoc }>('/startups', data);
  return r.startup;
};

export const getMyStartup = async (): Promise<StartupDoc | null> => {
  const r = await apiFetch.get<{ startup: StartupDoc | null }>('/startups/my');
  return r.startup;
};

export const updateMyStartup = async (data: Partial<StartupDoc> & Record<string, any>): Promise<StartupDoc> => {
  const r = await apiFetch.put<{ startup: StartupDoc }>('/startups/my', data);
  return r.startup;
};

export const uploadStartupDocument = async (file: File, title: string): Promise<StartupDoc> => {
  // Step 1: get presigned PUT URL from backend
  const presignRes = await apiFetch.post<{ presignedUrl: string; fileKey: string; fileUrl: string }>(
    '/startups/my/documents/presign',
    { fileName: file.name, contentType: file.type || 'application/octet-stream' }
  );

  // Step 2: upload directly to R2
  const tok = await getIdToken();
  const uploadRes = await fetch(presignRes.presignedUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
    body: file,
  });
  if (!uploadRes.ok) throw new Error('File upload to storage failed');

  // Suppress unused variable warning
  void tok;

  // Step 3: save metadata
  const r = await apiFetch.post<{ startup: StartupDoc }>('/startups/my/documents', {
    title: title || file.name,
    fileKey: presignRes.fileKey,
    fileUrl: presignRes.fileUrl,
    fileName: file.name,
    fileSize: file.size,
  });
  return r.startup;
};

export const deleteStartupDocument = async (docId: string): Promise<StartupDoc> => {
  const r = await apiFetch.delete<{ startup: StartupDoc }>(`/startups/my/documents/${docId}`);
  return r.startup;
};

export const getStartupById = async (id: string): Promise<StartupDoc> => {
  const r = await apiFetch.get<{ startup: StartupDoc }>(`/startups/${id}`);
  return r.startup;
};

export interface TimelineEvent {
  id: string;
  category: 'profile' | 'milestone' | 'achievement';
  date: string;
  title: string;
  summary?: string;
  badge?: string;
  achievementId?: string;
  value?: number;
  unit?: string;
  score?: number;
  achievementType?: string;
  valueFrom?: string;
  valueTo?: string;
}

export const getMyTimeline = async (): Promise<TimelineEvent[]> => {
  const r = await apiFetch.get<{ events: TimelineEvent[] }>('/startups/my/timeline');
  return r.events;
};
